import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAMSatelliteData } from '../../../../api/amSatelliteReport';
import SatelliteDataReportFooter from '../../../../components/SatelliteDataReport/SatelliteDataReportFooter';
import AmReportSatelliteIndexSection from './AmReportSatelliteIndexSection';
import AmReportSectionBox from './AmReportSectionBox';
import IrrigationInsightCard from '../../../../components/SatelliteCharts/IrrigationInsightCard';

const AMSatelliteReportDetailedPage = ({ farmId, accessKey }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [farmSatelliteData, setFarmSatelliteData] = useState(null);

  const fetchFarmSatelliteData = async (farmId, accessKey) => {
    try {
      const response = await getAMSatelliteData(farmId, accessKey);
      setFarmSatelliteData(response.data || null);
    } catch (err) {
      console.error('Error fetching satellite data:', err);
      setError(t('Failed to fetch satellite data'));
    }
  };

  useEffect(() => {
    if (!farmId || !accessKey) return;
    fetchFarmSatelliteData(farmId, accessKey);
  }, [farmId, !accessKey]);

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        mt: 1,
        '@media print': {
          margin: '0',
          padding: '0',
          backgroundColor: 'white !important',
        },
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        farmSatelliteData && (
          <Paper
            sx={{
              mb: 1,
              borderRadius: 0,
              boxShadow: 0,
              backgroundColor: '#237D410D',
              '@media print': {
                marginBottom: '0',
                boxShadow: 'none',
                backgroundColor: '#237D410D !important',
              },
            }}
          >
            {/* Soil Moisture in farm */}
            <AmReportSatelliteIndexSection
              farmSatelliteData={farmSatelliteData}
              title={t('Soil Moisture in farm')}
              activeTile="ndwi"
              id="soil-moisture-in-farm"
            />
            <IrrigationInsightCard
              insights={
                farmSatelliteData?.insights ||
                t('No irrigation insights available')
              }
              date={farmSatelliteData?.latest_satellite_image?.dt * 1000}
              id="irrigation-insight-card"
            />

            {/*Crop Health */}
            <AmReportSatelliteIndexSection
              farmSatelliteData={farmSatelliteData}
              title={t('Crop Health')}
              activeTile="ndvi"
              id="crop-health"
            />

            {/* Irrigation Planner */}
            <AmReportSectionBox
              title={t('Irrigation Planner')}
              farmId={farmId}
              sectionName="irrigation_planner"
              accessKey={accessKey}
              id="irrigation-planner"
            />

            {/* Soil moisture stats */}
            <AmReportSectionBox
              title={t('Date wise soil moisture stats')}
              farmId={farmId}
              sectionName="soil_moisture_stats"
              accessKey={accessKey}
              id="soil-moisture-stats"
            />

            {/* Crop growth stats */}
            <AmReportSectionBox
              title={t('Attainable vs Actual Crop Growth')}
              farmId={farmId}
              sectionName="crop_growth_stats"
              accessKey={accessKey}
              id="crop-growth-stats"
            />

            {/* Satellite Report Footer  */}
            <Paper
              sx={{
                borderRadius: 0,
                boxShadow: 0,
                mt: 8,
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
                '@media print': {
                  marginTop: '5mm',
                  boxShadow: 'none',
                  pageBreakBefore: 'auto',
                },
              }}
            >
              <SatelliteDataReportFooter />
            </Paper>
          </Paper>
        )
      )}
    </Box>
  );
};

export default AMSatelliteReportDetailedPage;
